import JSZip from 'jszip';

import { buildCsvString, downloadBlob, downloadCsv } from './csv-export.util';
import { extractSignatureOrFileUrl } from './form-submission-cell-format.util';
import { buildSubmissionsCsvExportPayload, getSubmissionExportKey } from './form-submissions-csv-export.util';

const SIGNATURES_FOLDER = 'signatures';

const isPathSafeChar = (c) => /[A-Za-z0-9_-]/.test(c);

/**
 * @param {string} segment
 * @returns {string}
 */
export const sanitizePathSegment = (segment) => {
  let acc = '';
  for (const ch of String(segment ?? '')) {
    acc += isPathSafeChar(ch) ? ch : '_';
  }
  const parts = acc.split('_').filter((p) => p.length > 0);
  const collapsed = parts.join('_');
  return collapsed === '' ? 'x' : collapsed;
};

/**
 * @param {string} url
 * @returns {string} file extension without dot
 */
const extensionFromDataUrl = (url) => {
  const dataMatch = /^data:image\/([^;]+);/i.exec(url);
  if (!dataMatch) {
    return null;
  }
  const t = dataMatch[1].toLowerCase();
  if (t === 'jpeg') {
    return 'jpg';
  }
  if (t === 'svg+xml') {
    return 'svg';
  }
  return t.replace(/\+xml$/i, '') || 'png';
};

const extensionFromHttpUrl = (url) => {
  try {
    const pathname = new URL(url).pathname;
    const last = pathname.split('.').pop();
    if (last && /^[a-zA-Z0-9]{1,8}$/.test(last)) {
      return last.toLowerCase();
    }
  } catch {
    // ignore invalid URL
  }
  return null;
};

export const guessSignatureFileExtension = (url) =>
  extensionFromDataUrl(url) ?? extensionFromHttpUrl(url) ?? 'png';

/**
 * Decode a `data:` URL body to bytes (supports `;base64,`).
 * @param {string} dataUrl
 * @returns {Uint8Array}
 */
export const dataUrlToUint8Array = (dataUrl) => {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) {
    throw new Error('Invalid data URL');
  }
  const meta = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  if (meta.includes(';base64') || meta.includes('base64')) {
    const binary = atob(payload);
    return Uint8Array.from(binary, (ch) => ch.codePointAt(0) ?? 0);
  }
  return new TextEncoder().encode(decodeURIComponent(payload));
};

/**
 * @param {string} url
 * @returns {Promise<Uint8Array>}
 */
export const fetchSignatureBinary = async (url) => {
  if (url.startsWith('data:')) {
    return dataUrlToUint8Array(url);
  }
  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`HTTP_${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
};

const csvFilenameToZipFilename = (csvName) => {
  const s = String(csvName ?? '');
  return s.toLowerCase().endsWith('.csv') ? `${s.slice(0, -4)}.zip` : `${s}.zip`;
};

/**
 * @param {*} zip JSZip instance
 * @param {readonly object[]} submissions
 * @param {ReadonlySet<string>} signatureKeys
 * @returns {Promise<Map<string, string>>} map key `${submissionKey}::${fieldKey}` → zip path
 */
const getSubmissionFieldSignatureUrl = (submission, fieldKey) => {
  const rawData = submission?.data;
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    return null;
  }
  return extractSignatureOrFileUrl(rawData[fieldKey]);
};

const buildSignatureZipPath = (submission, submissionIndex, fieldKey) => {
  const subKey = getSubmissionExportKey(submission, submissionIndex);
  const safeSub = sanitizePathSegment(subKey);
  const safeField = sanitizePathSegment(fieldKey);
  return { mapKey: `${subKey}::${fieldKey}`, zipPath: `${SIGNATURES_FOLDER}/${safeSub}_${safeField}` };
};

const writeSignatureToZip = async (zip, url, mapKey, zipPathBase, pathBySubmissionAndField) => {
  const ext = guessSignatureFileExtension(url);
  const zipPath = `${zipPathBase}.${ext}`;
  try {
    const bytes = await fetchSignatureBinary(url);
    zip.file(zipPath, bytes);
    pathBySubmissionAndField.set(mapKey, zipPath);
  } catch {
    // Leave map unset; CSV cell falls back to URL or placeholder via formatSubmissionDataForCsv.
  }
};

const tryAddOneSignatureFile = async (zip, submission, submissionIndex, fieldKey, pathBySubmissionAndField) => {
  const url = getSubmissionFieldSignatureUrl(submission, fieldKey);
  if (!url) {
    return;
  }
  const { mapKey, zipPath } = buildSignatureZipPath(submission, submissionIndex, fieldKey);
  await writeSignatureToZip(zip, url, mapKey, zipPath, pathBySubmissionAndField);
};

export const addSignatureFilesToZip = async (zip, submissions, signatureKeys) => {
  /** @type {Map<string, string>} */
  const pathBySubmissionAndField = new Map();
  if (signatureKeys?.size > 0) {
    for (let i = 0; i < submissions.length; i += 1) {
      const submission = submissions[i];
      for (const fieldKey of signatureKeys) {
        await tryAddOneSignatureFile(zip, submission, i, fieldKey, pathBySubmissionAndField);
      }
    }
  }
  return pathBySubmissionAndField;
};

/**
 * Build a zip containing the submissions CSV plus binary files for each resolved signature.
 * @param {{
 *   submissions: readonly object[],
 *   signatureKeys: ReadonlySet<string>,
 *   currencyMeta?: ReadonlyMap<string, string>,
 *   selectboxKeys?: ReadonlySet<string>,
 *   surveyKeys?: ReadonlySet<string>,
 *   datetimeKeys?: ReadonlySet<string>,
 *   formTitle: string,
 * }} input
 * @returns {Promise<void>}
 */
export const downloadFormSubmissionsZipArchive = async ({
  submissions,
  signatureKeys,
  currencyMeta = new Map(),
  selectboxKeys = new Set(),
  surveyKeys = new Set(),
  datetimeKeys = new Set(),
  formTitle,
}) => {
  const zip = new JSZip();
  const pathMap = await addSignatureFilesToZip(zip, submissions, signatureKeys);

  const { filename, headers, rows } = buildSubmissionsCsvExportPayload({
    submissions,
    signatureKeys,
    currencyMeta,
    selectboxKeys,
    surveyKeys,
    datetimeKeys,
    formTitle,
    signatureZipPathMap: pathMap,
  });

  zip.file(filename, buildCsvString(headers, rows));

  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  downloadBlob(csvFilenameToZipFilename(filename), blob);
};

/**
 * Exports all submissions as CSV, or zip when signature fields exist.
 * @param {ReturnType<import('./form-submission.util').buildFormSubmissionsExportInput>} exportInput
 */
export const exportFormSubmissionsArchive = async (exportInput) => {
  if (exportInput.signatureKeys.size > 0) {
    await downloadFormSubmissionsZipArchive(exportInput);
    return;
  }
  downloadCsv(buildSubmissionsCsvExportPayload(exportInput));
};
